import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ReviewStatus, resolveLocalized, type TemplateField } from "@arna-med/shared";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  FileDown,
  Loader2,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useConsultation } from "@/hooks/api/useConsultations";
import { useReport, useExportReportPdf } from "@/hooks/api/useReports";
import { useTemplate } from "@/hooks/api/useTemplates";

const KZ_SOAP_SECTIONS = [
  { key: "complaints", sectionLabel: "I", i18nKey: "report.kzsoap.complaints" },
  {
    key: "anamnesisDisease",
    sectionLabel: "II",
    i18nKey: "report.kzsoap.anamnesisDisease",
  },
  {
    key: "anamnesisLife",
    sectionLabel: "III",
    i18nKey: "report.kzsoap.anamnesisLife",
  },
  {
    key: "objectiveStatus",
    sectionLabel: "IV",
    i18nKey: "report.kzsoap.objectiveStatus",
  },
  {
    key: "localStatus",
    sectionLabel: "V",
    i18nKey: "report.kzsoap.localStatus",
  },
  { key: "diagnosis", sectionLabel: "VI", i18nKey: "report.kzsoap.diagnosis" },
  { key: "plan", sectionLabel: "VII", i18nKey: "report.kzsoap.plan" },
] as const;

export default function PatientConsultationView() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { data: consultation, isLoading: consultationLoading } = useConsultation(id);
  const { data: report, isLoading: reportLoading } = useReport(id, {
    enabled: consultation?.status === "completed",
  });
  const { data: template } = useTemplate(consultation?.templateId);
  const exportPdfMutation = useExportReportPdf(id);

  if (consultationLoading || reportLoading) {
    return (
      <Layout>
        <Spinner />
      </Layout>
    );
  }

  const mergedData = {
    ...(report?.structuredData ?? {}),
    ...(report?.editedData ?? {}),
  } as Record<string, unknown>;

  const mergedGeneral = {
    ...(report?.generalData ?? {}),
    ...(report?.generalEditedData ?? {}),
  } as Record<string, string | null | undefined>;

  // Prefer the snapshot captured on the report at generation time so template
  // edits don't retroactively change this report; fall back to the live template.
  const snapshotFields = report?.templateSnapshot?.fields as
    | TemplateField[]
    | undefined;
  const templateFields =
    snapshotFields && snapshotFields.length > 0
      ? snapshotFields
      : (template?.fields as TemplateField[] | undefined);
  const hasGeneralData = report?.generalData != null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/portal")}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{consultation?.patientName}</h1>
            <p className="text-sm text-muted-foreground">
              {consultation?.startedAt
                ? new Date(consultation.startedAt).toLocaleDateString()
                : ""}
            </p>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded-full ${
              consultation?.status === "completed"
                ? "bg-green-100 text-green-800"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {t(`dashboard.status.${consultation?.status}`)}
          </span>
        </div>

        {/* No report yet */}
        {consultation?.status !== "completed" && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              {t("portal.reportNotReady")}
            </CardContent>
          </Card>
        )}

        {consultation?.status === "completed" && report && (
          <>
            {/* Completed-by note (admin/manager finished another doctor's report) */}
            {consultation.completedById && consultation.completedByName && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 border rounded-md px-3 py-2">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                {t("portal.completedByNote", {
                  doctorName: consultation.doctorName ?? consultation.doctorId,
                  completedByName: consultation.completedByName,
                })}
              </div>
            )}

            {/* Review status + export */}
            <div className="flex items-center justify-between">
              <div>
                {report.reviewStatus === ReviewStatus.Draft ? (
                  <span className="flex items-center gap-1 text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                    <AlertTriangle className="h-4 w-4" />
                    {t("report.draft")}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-sm text-green-600 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("report.complete")}
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportPdfMutation.mutate(i18n.language)}
                disabled={exportPdfMutation.isPending}
              >
                <FileDown className="h-4 w-4 mr-2" />
                {exportPdfMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {t("report.export")}
              </Button>
            </div>

            {/* KZ-SOAP */}
            {hasGeneralData && (
              <Card>
                <CardContent className="space-y-4">
                  {KZ_SOAP_SECTIONS.map(({ key, sectionLabel, i18nKey }) => {
                    const value = mergedGeneral[key];
                    return (
                      <div key={key}>
                        <p className="font-semibold text-sm mb-1">
                          <span className="inline-flex items-center justify-center w-7 h-5 text-xs bg-primary/10 text-primary rounded mr-2 font-mono">
                            {sectionLabel}
                          </span>
                          {t(i18nKey)}
                        </p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {value || "—"}
                        </p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Specialized Template */}
            <Card>
              {hasGeneralData && (
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    {t("report.specializedTitle")}
                  </CardTitle>
                </CardHeader>
              )}
              <CardContent className="pt-6 space-y-5">
                {templateFields
                  ? templateFields.map((field) => {
                      const value = mergedData[field.key];
                      return (
                        <div key={field.key}>
                          <p className="font-semibold text-sm mb-1">
                            {resolveLocalized(field.label, i18n.language)}
                          </p>
                          {field.bilateral ? (
                            <div className="grid grid-cols-2 gap-3">
                              {(["right", "left"] as const).map((side) => (
                                <div key={side}>
                                  <p className="text-xs text-muted-foreground mb-1 capitalize">
                                    {t(`report.${side}`)}
                                  </p>
                                  <p className="text-sm">
                                    {((value ?? {}) as Record<string, string>)[
                                      side
                                    ] || "—"}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {String(value ?? "") || "—"}
                            </p>
                          )}
                        </div>
                      );
                    })
                  : Object.entries(mergedData).map(([key, value]) => (
                      <div key={key}>
                        <p className="font-semibold text-sm capitalize mb-1">
                          {key.replace(/([A-Z])/g, " $1")}
                        </p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {String(value ?? "") || "—"}
                        </p>
                      </div>
                    ))}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
